// FLAGS: health-data
// Changes a booked session's status from the sessions lists.
import { FC } from "react";
import {
  Dialog,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import { STATUS, STATUS_SUBJECT_FOR_CHANGE } from "@src/constants";
import { useFormik } from "formik";
import { Consult } from "@src/models/Consult";
import { useTranslation } from "react-i18next";
import { useConsultMutation } from "@src/queries/consults";
import { format } from "date-fns-tz";
import { ResponsePsychologistConsultInformationResponse } from "@src/api/v1/data-contracts";
import { DialogHeader, DialogActionButtons } from "../DialogToolkit";
import { WithSnackbarProps, withSnackbar } from "../SnackBarComponent";

interface ChangeSessionStatusDialogProps extends WithSnackbarProps {
  consult: ResponsePsychologistConsultInformationResponse;
  setOpen: (open: boolean) => void;
  open: boolean;
}
const ChangeSessionStatusDialog: FC<ChangeSessionStatusDialogProps> = ({
  consult,
  snackbarShowMessage,
  setOpen,
  open = false,
}) => {
  const { t } = useTranslation();
  const {
    updateConsultStatus: { mutateAsync: updateConsultStatus },
  } = useConsultMutation(consult.user.id);

  // if the status is Cancelled, we only want to show statuses that start with Cancelled
  const statusesForChange = STATUS_SUBJECT_FOR_CHANGE.filter(
    (status) =>
      consult.status?.startsWith("Cancelled") ===
      status.startsWith("Cancelled"),
  );

  const formik = useFormik({
    initialValues: {
      ...consult,
      status: consult.status,
    },
    // FLAGS: health-data
    // Changes the status of a real appointment; marking it Completed starts the notes-availability clock
    // quoted back in the toast. The finally closes the dialog even when the catch rethrows, so a rejected
    // change leaves nothing on screen.
    onSubmit: async (values: Consult) => {
      try {
        formik.setSubmitting(true);
        const response = await updateConsultStatus({ ...values });
        if (values.status === STATUS.COMPLETED) {
          snackbarShowMessage(
            `${t("AllSessions.StatusChangedAndNotesAvailable")} ${
              response.notesAvailableAt &&
              format(response.notesAvailableAt, "dd MMMM yy HH:mm")
            }`,
            "success",
            5000,
          );
        } else
          snackbarShowMessage(t("AllSessions.StatusChanged"), "success", 3000);
      } catch (e) {
        if (e.response.errors === undefined) {
          throw e;
        }

        Object.entries(e.response.errors).forEach(([key, value]) => {
          snackbarShowMessage(`${key}: ${value}`);
        });
      } finally {
        formik.setSubmitting(false);
        setOpen(false);
      }
    },
    enableReinitialize: true,
  });
  if (!consult) return null;

  return (
    <Dialog
      onClose={(e) => {
        formik.handleReset(e);
        setOpen(false);
      }}
      open={open}
    >
      <DialogHeader
        iconLigature="sync_alt"
        title={t("AllSessions.ChangeStatus")}
        outlined
        onDelete={undefined}
        onFilter={undefined}
        textRightButton={undefined}
      />

      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-wrap justify-between pb-4"
        noValidate
      >
        <FormControl
          variant="standard"
          className="basis-[calc(50%-12px)] h-18 py-2"
          required
        >
          <InputLabel id="status-label">{t("Common.Status")}</InputLabel>
          <Select
            variant="standard"
            name="status"
            labelId="status-label"
            id="status"
            onChange={formik.handleChange}
            value={formik.values.status}
            data-cy="status-select"
          >
            {statusesForChange.map((status) => {
              return (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </form>
      <DialogActionButtons
        onCancel={(e) => {
          formik.handleReset(e);
          setOpen(false);
        }}
        onSubmit={formik.submitForm}
        onCancelText={undefined}
        onSubmitText={t("Common.Save")}
        disabled={undefined}
      />
    </Dialog>
  );
};

export default withSnackbar(ChangeSessionStatusDialog);
